import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  BoardEditRequest,
  CommandRequest,
  ForgeRemoteApi,
  ManagerMessageRequest,
  OperationReceipt,
  ProjectionEnvelope,
} from "../api/forgeRemoteApi";
import { PortalApiError } from "../api/portalApi";
import type { ForgeRemoteDataSourceKind } from "../api/contracts";
import type { ForgeRemoteProjection, GatewayState, OperationState, PendingOperation } from "../domain";

const activationKey = (source: ForgeRemoteDataSourceKind) => ["forge-remote", source, "activation-preview"] as const;
const projectionKey = (source: ForgeRemoteDataSourceKind) => ["forge-remote", source, "projection"] as const;

interface ProjectionQueryView {
  projection: ForgeRemoteProjection;
  cursorReset: boolean;
  historyTruncated: boolean;
}

export function shouldReplaceProjection(current: ProjectionEnvelope | undefined, next: ProjectionEnvelope) {
  if (!current) return true;
  if (next.authorityEpoch !== current.authorityEpoch) {
    return next.authorityEpoch > current.authorityEpoch;
  }
  if (current.cursor === next.cursor) return false;
  return next.cursorRevision > current.cursorRevision;
}

export function reconcileProjectionEnvelope(
  current: ProjectionEnvelope | undefined,
  next: ProjectionEnvelope,
): ProjectionEnvelope {
  if (!current || shouldReplaceProjection(current, next)) return next;

  const currentSession = current.projection.session;
  const nextSession = next.projection.session;
  const isSameCanonicalProjection = next.authorityEpoch === current.authorityEpoch
    && next.cursorRevision === current.cursorRevision
    && next.cursor === current.cursor
    && currentSession.sessionId === nextSession.sessionId
    && currentSession.projection.cursor === current.cursor
    && nextSession.projection.cursor === next.cursor;
  if (!isSameCanonicalProjection) return current;

  return {
    ...current,
    projection: {
      ...current.projection,
      session: {
        ...currentSession,
        gateway: nextSession.gateway,
        host: nextSession.host,
        projection: {
          ...currentSession.projection,
          state: nextSession.projection.state,
        },
        commandAuthority: nextSession.commandAuthority,
      },
    },
  };
}

export function retainHistoryReset(current: boolean, envelope: Pick<ProjectionEnvelope, "cursorReset" | "historyTruncated">) {
  return current || envelope.cursorReset || envelope.historyTruncated;
}

export function operationFailureState(error: unknown): OperationState {
  if (error instanceof PortalApiError && error.status === 408) return "timed-out";
  if (error instanceof PortalApiError && error.status && error.status >= 400 && error.status < 500) return "rejected";
  return "outcome-unknown";
}

function abortableDelay(milliseconds: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    if (signal.aborted) return resolve();
    const timer = window.setTimeout(resolve, milliseconds);
    signal.addEventListener("abort", () => {
      window.clearTimeout(timer);
      resolve();
    }, { once: true });
  });
}

export function useActivationPreview(api: ForgeRemoteApi) {
  return useQuery({ queryKey: activationKey(api.sourceKind), queryFn: () => api.getActivationPreview(), staleTime: 60_000, retry: false });
}

export function useProjection(api: ForgeRemoteApi, enabled: boolean) {
  const queryClient = useQueryClient();
  const key = projectionKey(api.sourceKind);
  const [transportState, setTransportState] = useState<GatewayState>(
    api.sourceKind === "development-fixture" ? "connected" : "reconnecting",
  );
  const [sessionTerminal, setSessionTerminal] = useState(false);
  const [historyReset, setHistoryReset] = useState(false);
  const historyResetSessionId = useRef<string | null>(null);
  const lastActivityAt = useRef(Date.now());
  const markActivity = useCallback(() => {
    lastActivityAt.current = Date.now();
    setTransportState("connected");
  }, []);
  const query = useQuery<ProjectionEnvelope, Error, ProjectionQueryView>({
    queryKey: key,
    queryFn: async () => {
      const next = await api.getCurrentProjection();
      const current = queryClient.getQueryData<ProjectionEnvelope>(key);
      return reconcileProjectionEnvelope(current, next);
    },
    select: (envelope) => ({
      projection: envelope.projection,
      cursorReset: envelope.cursorReset,
      historyTruncated: envelope.historyTruncated,
    }),
    enabled,
    staleTime: 5_000,
    refetchInterval: enabled ? (api.sourceKind === "development-fixture" ? 15_000 : 60_000) : false,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!enabled) {
      setSessionTerminal(false);
      return;
    }
    if (query.data) markActivity();
  }, [enabled, markActivity, query.data]);

  useEffect(() => {
    if (!enabled || !query.data) {
      historyResetSessionId.current = null;
      setHistoryReset(false);
      return;
    }

    const sessionId = query.data.projection.session.sessionId;
    if (historyResetSessionId.current !== sessionId) {
      historyResetSessionId.current = sessionId;
      setHistoryReset(retainHistoryReset(false, query.data));
      return;
    }

    setHistoryReset((current) => retainHistoryReset(current, query.data!));
  }, [enabled, query.data]);

  useEffect(() => {
    if (!enabled || api.sourceKind === "development-fixture") return;
    const timer = window.setInterval(() => {
      if (Date.now() - lastActivityAt.current > 45_000) setTransportState("offline");
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [api.sourceKind, enabled]);

  useEffect(() => {
    if (!enabled || !api.supportsProjectionStream || !query.data?.projection.session.sessionId) return;
    const controller = new AbortController();
    let cursor = queryClient.getQueryData<ProjectionEnvelope>(key)?.cursor ?? query.data.projection.session.projection.cursor;
    let reconnectDelay = 1_000;

    const apply = (next: ProjectionEnvelope) => {
      markActivity();
      const current = queryClient.getQueryData<ProjectionEnvelope>(key);
      const reconciled = reconcileProjectionEnvelope(current, next);
      if (reconciled === current) return;
      cursor = reconciled.cursor;
      setHistoryReset((wasReset) => retainHistoryReset(wasReset, reconciled));
      queryClient.setQueryData(key, reconciled);
    };

    const restore = async () => {
      const next = await api.getCurrentProjection();
      apply(next);
    };

    const run = async () => {
      while (!controller.signal.aborted) {
        try {
          await api.streamProjectionUpdates({
            cursor,
            signal: controller.signal,
            onProjection: apply,
            onActivity: markActivity,
          });
          reconnectDelay = 1_000;
        } catch (error) {
          if (controller.signal.aborted) return;
          if (error instanceof PortalApiError && [401, 403].includes(error.status ?? 0)) {
            setTransportState("offline");
            setSessionTerminal(true);
            return;
          }
          if (error instanceof PortalApiError && error.status === 410) {
            try {
              await restore();
              reconnectDelay = 1_000;
              continue;
            } catch {
              // A normal backoff follows; the last trusted projection remains rendered.
            }
          }
        }
        setTransportState(Date.now() - lastActivityAt.current > 45_000 ? "offline" : "reconnecting");
        await abortableDelay(reconnectDelay, controller.signal);
        reconnectDelay = Math.min(reconnectDelay * 2, 5_000);
      }
    };

    void run();
    return () => controller.abort();
  }, [api, api.sourceKind, enabled, markActivity, query.data?.projection.session.sessionId, queryClient]);

  return { ...query, data: query.data?.projection, transportState, sessionTerminal, historyReset };
}

export function useActivate(api: ForgeRemoteApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.redeemActivation(),
    onSuccess: () => queryClient.removeQueries({ queryKey: ["forge-remote", api.sourceKind] }),
  });
}

export function useRemoteActions(api: ForgeRemoteApi) {
  const queryClient = useQueryClient();
  const key = projectionKey(api.sourceKind);
  const [operations, setOperations] = useState<Record<string, PendingOperation>>({});

  const stage = useCallback((operation: PendingOperation) => {
    setOperations((current) => ({ ...current, [operation.id]: operation }));
  }, []);

  const acceptReceipt = useCallback(async (operation: PendingOperation, receipt: OperationReceipt) => {
    if (receipt.operationId !== operation.id) throw new PortalApiError("Operation receipt correlation mismatch.");
    stage({ ...operation, state: receipt.state });
    await queryClient.invalidateQueries({ queryKey: key });
    return receipt;
  }, [key, queryClient, stage]);

  const fail = useCallback((operation: PendingOperation, error: unknown) => {
    stage({ ...operation, state: operationFailureState(error) });
  }, [stage]);

  const sendMessage = useMutation<OperationReceipt, Error, ManagerMessageRequest>({
    mutationFn: async (request) => {
      const operation: PendingOperation = {
        id: request.operationId,
        action: "manager.chat",
        targetId: request.messageId,
        state: "requested",
        issuedAt: new Date().toISOString(),
      };
      stage(operation);
      try {
        return await acceptReceipt(operation, await api.sendManagerMessage(request));
      } catch (error) {
        fail(operation, error);
        throw error;
      }
    },
  });

  const command = useMutation<OperationReceipt, Error, CommandRequest>({
    mutationFn: async (request) => {
      const operation: PendingOperation = {
        id: request.operationId,
        action: request.action,
        targetId: request.targetId,
        state: "requested",
        issuedAt: new Date().toISOString(),
      };
      stage(operation);
      try {
        return await acceptReceipt(operation, await api.submitCommand(request));
      } catch (error) {
        fail(operation, error);
        throw error;
      }
    },
  });

  const editBoard = useMutation<OperationReceipt, Error, BoardEditRequest>({
    mutationFn: async (request) => {
      const operation: PendingOperation = {
        id: request.operationId,
        action: "board.edit",
        targetId: request.cardId,
        state: "requested",
        issuedAt: new Date().toISOString(),
      };
      stage(operation);
      try {
        return await acceptReceipt(operation, await api.editBoardItem(request));
      } catch (error) {
        fail(operation, error);
        throw error;
      }
    },
  });

  const operationsByTarget = useMemo(() => Object.values(operations).reduce<Record<string, PendingOperation>>((latest, operation) => {
    const current = latest[operation.targetId];
    if (!current || operation.issuedAt >= current.issuedAt) latest[operation.targetId] = operation;
    return latest;
  }, {}), [operations]);

  return { operations, operationsByTarget, sendMessage, command, editBoard };
}

export type RemoteActions = ReturnType<typeof useRemoteActions>;
